import React, { useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { getQuickSightEmbedUrl } from '../../utils/quicksight';

const QNavCustomerAnalyticsDashboard: React.FC = () => {
  const { user } = React.useContext(AuthContext) || { user: null };
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      initializeQuickSightDashboard();
    }
  }, [user]);

  const initializeQuickSightDashboard = async () => {
    try {
      console.log('🚀 Starting QuickSight dashboard initialization...');
      setIsLoading(true);
      setError(null);

      console.log('📡 Calling getQuickSightEmbedUrl...');
      const url = await getQuickSightEmbedUrl('7fb858b1-b76c-439e-8e99-24286bfef57c');
      console.log('✅ Received embed URL:', url);
      
      if (url) {
        console.log('🖼️ Setting embed URL...');
        setEmbedUrl(url);
        console.log('✅ Dashboard URL set successfully');
      } else {
        throw new Error('No embed URL received from QuickSight');
      }
    } catch (err) {
      console.error('❌ Error initializing QuickSight dashboard:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setIsLoading(false);
      console.log('🏁 QuickSight initialization complete');
    }
  };

  const handleRetry = () => {
    initializeQuickSightDashboard();
  };

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center bg-surface-light rounded-qnav">
          <div className="text-center">
            <div className="qnav-loading-spinner mx-auto mb-4"></div>
            <p className="text-secondary-600">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex items-center justify-center bg-surface-light rounded-qnav">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-secondary-900 mb-2">
              Dashboard Unavailable
            </h3>
            <p className="text-secondary-600 mb-4">
              {error}
            </p>
            <button
              onClick={handleRetry}
              className="qnav-btn-primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 bg-surface-light rounded-qnav overflow-hidden">
        {embedUrl && (
          <iframe
            src={embedUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', minHeight: '600px' }}
            onLoad={() => console.log('✅ Iframe loaded successfully')}
            onError={(error) => console.error('❌ Iframe load error:', error)}
          />
        )}
      </div>
      
      {/* Dashboard Controls */}
      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-secondary-500">
          Last updated: {new Date().toLocaleString()}
        </div>
        <button
          onClick={handleRetry}
          className="qnav-btn-outline text-sm"
        >
          Refresh Dashboard
        </button>
      </div>
    </div>
  );
};

export { QNavCustomerAnalyticsDashboard };
